import { useQuery } from '@tanstack/react-query';
import * as installationApi from '@/api/installation.api';

/**
 * S-11 · Zona como CATÁLOGO CONTROLADO (tanda EMT-CNV-2026-01).
 *
 * La zona se elige de la lista que el dueño edita en Configuración → CRM, no se
 * escribe. En texto libre, "Santo Domingo", "sto dgo" y "SD" son tres zonas
 * distintas para cualquier reporte.
 *
 * NO se normaliza el histórico automáticamente: un trabajo viejo con una zona
 * que ya no está en la lista conserva la suya y se muestra como una opción
 * extra, marcada. Reescribir el histórico sin que el dueño apruebe las
 * agrupaciones sería decidir por él.
 */
export function ZonaSelect({
  value,
  onChange,
  label = 'Zona',
}: {
  value: string;
  onChange: (zona: string) => void;
  label?: string;
}) {
  const cfg = useQuery({ queryKey: ['installation', 'crm'], queryFn: installationApi.getCrmView });
  const zonas = cfg.data?.crm?.zonas ?? [];
  // La zona que ya tiene el trabajo se conserva aunque no esté en el catálogo:
  // si no, abrir la ficha de un trabajo viejo le borraría la zona en silencio.
  const fueraDeCatalogo = value !== '' && !zonas.includes(value);

  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
        {label}
      </span>
      <select
        className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">Sin zona</option>
        {zonas.map((z) => (
          <option key={z} value={z}>
            {z}
          </option>
        ))}
        {fueraDeCatalogo && <option value={value}>{value} (fuera de la lista)</option>}
      </select>
      {fueraDeCatalogo && (
        <span className="mt-1 block text-xs text-amber-700">
          Esta zona no está en la lista actual. Se conserva tal cual; para usarla en trabajos
          nuevos, agréguela en Configuración → CRM.
        </span>
      )}
      {zonas.length === 0 && !cfg.isLoading && (
        <span className="mt-1 block text-xs text-slate-500">
          No hay zonas cargadas. Agréguelas en Configuración → CRM.
        </span>
      )}
    </label>
  );
}
